import { Component, OnInit, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";

import { Config } from "src/app/config";
import { ExamService } from "src/app/services/exam.service";
import { EventsHubService, EditorMessage, EDITORMESSAGETYPE } from "src/app/services/eventshub.service";
import { ModalDialogService } from "src/app/modals/modaldialog/modaldialog.service";
import { AdminAuthService } from "src/app/services/adminauth.service";
import { MODALDIALOGSIZE } from "src/app/modals/modaldialog/modaldialog.component";
import { ExamPropertiesComponent } from "src/app/modals/examproperties/examproperties.component";
import { ExamMenuEditComponent } from "src/app/modals/exammenuedit/exammenuedit.component";
import { LibraryImagesComponent } from "src/app/modals/libraryimages/libraryimages.component";
import { LibraryVideosComponent } from "src/app/modals/libraryvideos/libraryvideos.component";
import { LibrarySoundsComponent } from "src/app/modals/librarysounds/librarysounds.component";
import { ServerResponseExamSave } from "src/app/models/serverresponse_examsave";
import { SERVERRESPONSERESULT } from "src/app/models/serverresponse";
import { Exam } from "src/app/models/exam";

@Component({
  selector: "hkt-edittoolbar",
  templateUrl: "./edittoolbar.component.html",
  styleUrls: ["./edittoolbar.component.scss"],
})
export class EditToolBarComponent implements OnInit, OnDestroy {
  toolbarMenuActive: string;
  exam: Exam;
  examSubscription: Subscription;

  constructor(
    private examService: ExamService,
    private modalDialogService: ModalDialogService,
    private adminAuthService: AdminAuthService,
    private eventsHubService: EventsHubService,
  ) {
    this.toolbarMenuActive = "Exam";
  }

  ngOnInit(): void {
    this.exam = this.examService.currentExam;
    this.examSubscription = this.examService.onCurrentExamChange.subscribe(exam => (this.exam = exam));
  }

  ngOnDestroy(): void {
    this.examSubscription.unsubscribe();
  }

  onToolbar(selected: string): boolean {
    this.toolbarMenuActive = selected;
    return false;
  }

  //---------------------------------------------
  // Exam Menu
  //---------------------------------------------
  onExamSave(): void {
    this.examService.saveEditExam().subscribe(
      (response: ServerResponseExamSave) => {
        if (response.result == SERVERRESPONSERESULT.OK) {
          this.modalDialogService.showAlert(Config.APP_NAME, "Exam Saved");
        } else if (response.result == SERVERRESPONSERESULT.NotLoggedIn) {
          this.adminAuthService.askLogin(Config.ADMIN_SESSION_ENDED).subscribe();
        } else if (response.result == SERVERRESPONSERESULT.Error) {
          this.modalDialogService.showAlert(Config.APP_NAME, response.error);
        }
      },
      error => {
        console.error(error);
      },
    );
  }

  onExamProperties(): void {
    const modal = this.modalDialogService.createModal();
    modal.setTitle("Exam Properties");
    const component = modal.setBodyComponent(ExamPropertiesComponent);
    modal.setButtonCancelOk();
    component.onFormStatusChanges.subscribe(invalid => {
      modal.buttons[1].disabled = invalid;
    });
    modal.show().subscribe(
      button => {
        if (button.index == 0) {
          modal.close();
        } else if (button.index == 1) {
          if (component.hasErrors()) {
            return;
          }
          const formValues = component.values;
          this.exam.name = formValues.name;
          this.exam.description = formValues.description;
          this.exam.order = formValues.order;
          this.exam.enable = formValues.enable;
          this.exam.pageWidth = +formValues.pageWidth;
          this.exam.pageHeight = +formValues.pageHeight;
          this.exam.style = formValues.style;
          this.exam.disableSubmit = formValues.disableSubmit === "true";
          modal.close();
        }
      },
      error => {},
      () => {
        this.modalDialogService.dismissModal(modal);
      },
    );
  }

  onExamMenu(): void {
    const modal = this.modalDialogService.createModal();
    modal.setTitle("Exam Menu");
    const component = modal.setBodyComponent(ExamMenuEditComponent);
    modal.setButtonCancelOk();
    component.onFormStatusChanges.subscribe(invalid => {
      modal.buttons[1].disabled = invalid;
    });
    modal.show().subscribe(
      button => {
        if (button.index == 0) {
          modal.close();
        } else if (button.index == 1) {
          if (component.hasErrors()) {
            return;
          }
          this.exam.menu = component.value;
          modal.close();
        }
      },
      error => {},
      () => {
        this.modalDialogService.dismissModal(modal);
      },
    );
  }

  onExamTest(): void {
    window.open("/exam/" + this.exam.id, "_blank");
  }

  //---------------------------------------------
  // View
  //---------------------------------------------
  onView100(): void {
    let msg = new EditorMessage();
    msg.type = EDITORMESSAGETYPE.View100;
    this.eventsHubService.setEditorMessage(msg);
  }

  onViewFit(): void {
    let msg = new EditorMessage();
    msg.type = EDITORMESSAGETYPE.ViewFit;
    this.eventsHubService.setEditorMessage(msg);
  }

  //---------------------------------------------
  // Library
  //---------------------------------------------
  onLibraryImages(): void {
    const modal = this.modalDialogService.createModal();
    modal.setTitle("Library: Images");
    modal.setSize(MODALDIALOGSIZE.Large);
    modal.setBodyComponent(LibraryImagesComponent);
    modal.addButton("Close", "btn btn-primary", () => {
      modal.close();
    });
    modal.show().subscribe({
      complete: () => this.modalDialogService.dismissModal(modal),
    });
  }

  onLibraryVideos(): void {
    const modal = this.modalDialogService.createModal();
    modal.setTitle("Library: Videos");
    modal.setSize(MODALDIALOGSIZE.Large);
    modal.setBodyComponent(LibraryVideosComponent);
    modal.addButton("Close", "btn btn-primary", () => {
      modal.close();
    });
    modal.show().subscribe({
      complete: () => this.modalDialogService.dismissModal(modal),
    });
  }

  onLibrarySounds(): void {
    const modal = this.modalDialogService.createModal();
    modal.setTitle("Library: Sounds");
    modal.setSize(MODALDIALOGSIZE.Large);
    modal.setBodyComponent(LibrarySoundsComponent);
    modal.addButton("Close", "btn btn-primary", () => {
      modal.close();
    });
    modal.show().subscribe({
      complete: () => this.modalDialogService.dismissModal(modal),
    });
  }

  onLibraryFonts(): void {
    // TODO
  }

  //---------------------------------------------
  // Change Pages
  //---------------------------------------------
  onPagesNewPage(): void {
    this.exam.addPage();
    this.exam.currentPageIndex = this.exam.pages.length - 1;

    let msg = new EditorMessage();
    msg.type = EDITORMESSAGETYPE.SelectPage;
    msg.data = this.exam.pages[this.exam.currentPageIndex];
    this.eventsHubService.setEditorMessage(msg);
  }

  onPagesPreviousPage(): void {
    if (this.exam.currentPageIndex == 0) {
      return;
    }
    this.exam.currentPageIndex--;

    let msg = new EditorMessage();
    msg.type = EDITORMESSAGETYPE.SelectPage;
    msg.data = this.exam.pages[this.exam.currentPageIndex];
    this.eventsHubService.setEditorMessage(msg);
  }

  onPagesNextPage(): void {
    if (this.exam.currentPageIndex >= this.exam.pages.length - 1) {
      return;
    }
    this.exam.currentPageIndex++;

    let msg = new EditorMessage();
    msg.type = EDITORMESSAGETYPE.SelectPage;
    msg.data = this.exam.pages[this.exam.currentPageIndex];
    this.eventsHubService.setEditorMessage(msg);
  }

  //---------------------------------------------
  // New Objects
  //---------------------------------------------
  onObjectsContainer(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectContainer(this.exam.style);
  }

  onObjectsText(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectText(this.exam.style);
  }

  onObjectsLink(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectLink(this.exam.style);
  }

  onObjectsList(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectList(this.exam.style);
  }

  onObjectsImage(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectImage(this.exam.style);
  }

  onObjectsVideo(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectVideo(this.exam.style);
  }

  onObjectsSound(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectSound(this.exam.style);
  }

  onObjectsButton(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectButton(this.exam.style);
  }

  onObjectsSelection(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectSelection(this.exam.style);
  }

  onObjectsInput(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectInput(this.exam.style);
  }

  onObjectsArrow(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectArrow(this.exam.style);
  }

  onObjectsUploadButton(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectUploadButton(this.exam.style);
  }

  onObjectsEvaluation(): void {
    if (this.exam == null) {
      return;
    }
    this.exam.pages[this.exam.currentPageIndex].createPageObjectEvaluation(this.exam.style);
  }
}
